import { isJSON } from "@/lib/util";
import { TransactionReceipt } from "@ethersproject/abstract-provider";
import { BigNumber } from "@ethersproject/bignumber";

export interface EthersTransactionInnerError {
  reason: string;
  code: number;
  body: string;
  requestBody: string;
  requestMethod: string;
  url: string;
  error: {
    code: number;
    data: string;
  }
}

export interface EthersTransactionError {
  replacement: {
    blockhash: string;
    hash: string;
    wait: (blocks: number) => Promise<TransactionReceipt>;
  };
  cancelled?: boolean; // undefined if not cancelled
  code: string | number;
  message: string;
  reason: string;
  method?: string; // e.g. "estimateGas"
  transaction: {
    from: string;
    to: string;
    gasLimit: BigNumber;
    value: BigNumber;
    data: string;
  };
  error: EthersTransactionInnerError;
}

export const parseEthersTransactionError = (err: EthersTransactionError): string => {
  let msg;
  if (err.code === 4001 || err.code === 'ACTION_REJECTED') {
    msg = 'Transaction rejected';
  } else if (err.code === 'INSUFFICIENT_FUNDS') {
    msg = 'Not enough funds in wallet';
  } else if (err.cancelled) {
    msg = 'Transaction was cancelled';
  } else if (err.code === 'TRANSACTION_REPLACED' && err.replacement) {
    msg = `Transaction was sped up and replaced. See https://etherscan.io/tx/${err.replacement.hash}`;
  } else if (err.code === 'CALL_EXCEPTION') {
    msg = 'Transaction Failed';
  } else if (err.message && err.message.includes('Ledger device')) {
    msg = 'Error with your ledger device, please try again';
  } else if (err.message && err.message.includes('Maximum tokens already minted')) {
    msg = 'Maximum tokens already minted for this wallet per allocation';
  } else if (err.code === 'UNPREDICTABLE_GAS_LIMIT' && err.reason) {
    return 'Could not estimate gas limit: ' + err.reason;
  } else if (err.reason) {
    msg = 'Error: ' + err.reason;
  } else if (err.message) {
    msg = err.message;
  } else if (typeof err === 'string') {
    msg = err;
  } else if (err.error && err.error.body && isJSON(err.error.body)) {
    // Further parsing of the error.body is required to attempt to get a human readable message.
    msg = JSON.parse(err.error.body).error?.message?.replace('execution reverted: ', '')
      || 'The simulation for your transaction predicted a failure. Refresh the page and try again.';
  } else {
    msg = 'Transaction unable to complete, contact support on the Forum';
  }

  return msg;
};

